import { Injectable } from '@angular/core';
import { db } from '@ih/app/client/shared/db';

export interface FormDraftRecord {
  /** Composite key: `${userId}::${formId}` */
  id: string;
  userId: string;
  formId: string;
  /** Snapshot of `state.form.formAnswers.model` at save time. */
  answers: Record<string, unknown>;
  /**
   * Snapshot of `UpdateFormState.formAnswersForeignValues` at save time.
   * Used to restore human-readable display values for LOOKUP question types
   * without having to re-query the server (works offline).
   */
  foreignValues?: Record<string, unknown>;
  /**
   * Per-draft UUID used to scope `<projectId>/__pending__/<draftId>/...`
   * uploads. Generated on first call to `ensureDraftId` and cleared by
   * `clearDraftId` after a successful submission (REQ-1, REQ-8 of feature
   * `submit-pending-file-move`).
   */
  draftId?: string;
  /** ISO 8601 timestamp of the save. */
  updatedAt: string;
}

@Injectable({ providedIn: 'root' })
export class FormDraftService {
  /** Build the composite primary key. Exposed for tests. */
  key(userId: string, formId: string): string {
    return `${userId}::${formId}`;
  }

  async get(
    userId: string,
    formId: string,
  ): Promise<FormDraftRecord | undefined> {
    return db.formDrafts.get(this.key(userId, formId));
  }

  async put(record: Omit<FormDraftRecord, 'id'>): Promise<void> {
    const id = this.key(record.userId, record.formId);
    await db.formDrafts.put({ ...record, id }, id);
  }

  async delete(userId: string, formId: string): Promise<void> {
    await db.formDrafts.delete(this.key(userId, formId));
  }

  async list(userId: string): Promise<FormDraftRecord[]> {
    return db.formDrafts.where('userId').equals(userId).toArray();
  }

  /**
   * Returns the existing `draftId` for `(userId, formId)` or generates,
   * persists and returns a fresh crypto-strong UUID (REQ-1).
   *
   * If no draft record exists yet, creates a stub record containing only
   * the new `draftId` so subsequent `get`/`put` calls preserve it.
   */
  async ensureDraftId(userId: string, formId: string): Promise<string> {
    const id = this.key(userId, formId);
    const existing = (await db.formDrafts.get(id)) as
      | FormDraftRecord
      | undefined;
    if (existing && existing.draftId) {
      return existing.draftId;
    }
    const draftId = this.generateUuid();
    const next: FormDraftRecord = {
      id,
      userId,
      formId,
      answers: existing?.answers ?? {},
      foreignValues: existing?.foreignValues,
      draftId,
      updatedAt: existing?.updatedAt ?? new Date().toISOString(),
    };
    await db.formDrafts.put(next, id);
    return draftId;
  }

  /**
   * Clears only the `draftId` on the draft record so the next call to
   * `ensureDraftId` mints a fresh UUID (REQ-8). The rest of the draft is
   * left untouched — call `delete` if the whole draft should go.
   */
  async clearDraftId(userId: string, formId: string): Promise<void> {
    const id = this.key(userId, formId);
    const existing = (await db.formDrafts.get(id)) as
      | FormDraftRecord
      | undefined;
    if (!existing) return;
    if (!existing.draftId) return;
    const { draftId: _drop, ...rest } = existing;
    void _drop;
    await db.formDrafts.put({ ...rest, id }, id);
  }

  /** Crypto-strong UUID v4. Falls back to a Math.random-based v4-shaped
   *  string in environments without `crypto.randomUUID` (older Safari). */
  private generateUuid(): string {
    const g = globalThis as {
      crypto?: { randomUUID?: () => string };
    };
    if (g.crypto && typeof g.crypto.randomUUID === 'function') {
      return g.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
